import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Text,
  Icon,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import ModalInput from '../forms/modalForms/modalInput'
import { fetchEmployeeList, addTeam } from '../../api/workforce/workforce'
import { WarningIcon } from '../../assets/icons'
import { phoneNumbers, countrySelectOptions } from '../../components/countries/PhoneDial'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useFormMutation } from '../../hooks/useFormMutation'
import ModalSelect from '../forms/modalForms/modalSelect'
import ModalMultiSelect from '../forms/modalForms/modalMultiSelect'
import FormModal from '../common/FormModal'

interface AddTeamsModalProps {
  isOpen: boolean
  onClose: () => void
}

interface SelectedUser {
  id: string
  first_name: string
  last_name: string
  profile_picture: string
}

const AddTeamsModal: React.FC<AddTeamsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()

  // State for team
  const [team, setTeam] = useState<{
    name: string
    team_manager: string
    country: string
    members: SelectedUser[]
    description: string
  }>({
    name: '',
    team_manager: '',
    country: '',
    members: [],
    description: '',
  })

  // Validation errors
  const [errors, setErrors] = useState({
    teamName: '',
    country: '',
    manager: '',
    members: '',
  })

  // Pagination and search state for manager selection
  const [managerSearch, setManagerSearch] = useState('')
  const [selectedManagerOption, setSelectedManagerOption] = useState<{
    value: string
    label: string
    profile_picture?: string
  } | null>(null)
  const [memberSearch, setMemberSearch] = useState('')
  const [memberPage, setMemberPage] = useState(1)
  const pageSize = 10

  // Query for managers with infinite scroll + search
  const managersQuery = useInfiniteQuery({
    queryKey: ['teamManagers', managerSearch, pageSize],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({
        search: managerSearch,
        page: pageParam,
        pageSize: pageSize,
        filters: {},
        minimal: true,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: isOpen,
  })

  // Query for team members with pagination and search
  const membersQuery = useQuery({
    queryKey: ['teamMembers', memberSearch, memberPage, pageSize],
    queryFn: () => fetchEmployeeList({
      search: memberSearch,
      page: memberPage,
      pageSize: pageSize,
      filters: {},
      // only request minimal fields for member lookup
      minimal: true,
    }),
    enabled: isOpen, // Only run when modal is open
  })

  const managersList = useMemo(() => {
    const pages = managersQuery.data?.pages ?? []
    return pages.flatMap((p: any) => (Array.isArray(p?.data) ? p.data : []))
  }, [managersQuery.data?.pages])

  // Format manager options for dropdown
  const managerOptions = useMemo(() => {
    const options = managersList.map((manager: any) => ({
      value: String(manager.id),
      label: `${manager.first_name} ${manager.last_name}`,
      profilePicture: manager.profile_picture,
    }))

    if (selectedManagerOption && !options.some((o) => o.value === selectedManagerOption.value)) {
      options.unshift({
        value: selectedManagerOption.value,
        label: selectedManagerOption.label,
        profilePicture: selectedManagerOption.profile_picture,
      })
    }

    return options
  }, [managersList, selectedManagerOption])

  // Format member options for multi-select
  const memberOptions = membersQuery.data?.data?.map((member: any) => ({
    key: member.id,
    name: `${member.first_name} ${member.last_name}`,
    profile_picture: member.profile_picture,
  })) || []

  useEffect(() => {
    if (memberSearch) {
      setMemberPage(1)
    }
  }, [memberSearch])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setTeam((prev) => ({ ...prev, [name]: value }))
  }

  const isValid = () => {
    const newErrors = {
      teamName:
        team.name.trim() === ''
          ? t('validation.required', { field: t('workforce.teams.teamName') })
          : '',
      country:
        team.country.length === 0
          ? t('validation.required', { field: t('common.fields.location') })
          : '',
      manager:
        team.team_manager === ''
          ? t('validation.required', { field: t('workforce.teams.teamManager') })
          : '',
      members:
        team.members.length === 0
          ? t('validation.required', { field: t('workforce.teams.members') })
          : '',
    }
    setErrors(newErrors)
    return !Object.values(newErrors).some((error) => error)
  }

  const handleClose = () => {
    setTeam({
      name: '',
      team_manager: '',
      country: '',
      members: [],
      description: '',
    })
    setErrors({
      teamName: '',
      country: '',
      manager: '',
      members: '',
    })
    setManagerSearch('')
    setSelectedManagerOption(null)
    setMemberSearch('')
    setMemberPage(1)
    onClose()
  }

  const createTeam = useFormMutation({
    mutationFn: addTeam,
    queryKey: 'teams',
    successMessage: t('messages.createSuccess'),
    onClose: handleClose,
  })

  const handleSubmit = async () => {
    if (isValid()) {
      const countryCode =
        phoneNumbers.find((country) => country.name === team.country)?.code.toUpperCase() || team.country
      const memberIds = new Set(team.members.map((m) => m.id))
      if (team.team_manager) memberIds.add(team.team_manager)
      const updatedTeam = {
        ...team,
        country: countryCode,
        team_manager: team.team_manager,
        members: Array.from(memberIds),
      }

      createTeam.mutate(updatedTeam)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workforce.teams.addNewTeam')}
      onSubmit={handleSubmit}
      isLoading={createTeam.isPending}
      submitLabel={t('common.actions.add')}
      closeOnOverlayClick={false}
    >
          <VStack spacing={4} w="100%">
            <Box width="100%">
              <ModalInput
                label={t('workforce.teams.teamName')}
                type="text"
                name="name"
                placeholder={t('workforce.teams.placeholders.enterTeamName')}
                value={team.name}
                onChange={handleChange}
                required
              />
              {errors.teamName && (
                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                  <Text>
                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                      <WarningIcon />
                    </Icon>
                    {errors.teamName}
                  </Text>
                </Box>
              )}
            </Box>
            <Box width="100%">
              <ModalSelect
                label={t('common.fields.location')}
                name="country"
                value={team.country}
                options={countrySelectOptions}
                onChange={(e) => setTeam((prev) => ({ ...prev, country: e.target.value }))}
                showCountryFlag
              />
              {errors.country && (
                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                  <Text>
                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                      <WarningIcon />
                    </Icon>
                    {errors.country}
                  </Text>
                </Box>
              )}
            </Box>
            <Box width="100%">
              <ModalSelect
                label={t('workforce.teams.teamManager')}
                name="team_manager"
                value={team.team_manager || ''}
                options={managerOptions}
                hasProfilePicture
                onChange={(e) => {
                  const managerId = e.target.value
                  const selectedManager = managersList.find((m: any) => String(m.id) === String(managerId))
                  if (selectedManager) {
                    setSelectedManagerOption({
                      value: String(selectedManager.id),
                      label: `${selectedManager.first_name} ${selectedManager.last_name}`,
                      profile_picture: selectedManager.profile_picture,
                    })
                  }
                  setTeam((prev) => ({
                    ...prev,
                    team_manager: managerId,
                  }))
                }}
                onSearch={(term) => setManagerSearch(term)}
                isLoading={managersQuery.isLoading}
                onLoadMore={() => managersQuery.fetchNextPage()}
                hasMore={!!managersQuery.hasNextPage}
                isLoadingMore={managersQuery.isFetchingNextPage}
                placeholder={t('common.labels.searchPlaceholder')}
              />
              {errors.manager && (
                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                  <Text>
                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                      <WarningIcon />
                    </Icon>
                    {errors.manager}
                  </Text>
                </Box>
              )}
            </Box>
            <Box width="100%">
              <ModalMultiSelect
                label={t('workforce.teams.members')}
                options={memberOptions}
                selectedValues={team.members.map((member) => ({
                  key: member.id,
                  name: `${member.first_name} ${member.last_name}`,
                  profile_picture: member.profile_picture,
                }))}
                onChange={(selectedList) => {
                  setTeam((prev) => ({
                    ...prev,
                    members: selectedList.map((item) => ({
                      id: item.key,
                      first_name: item.name?.split(' ')[0],
                      last_name: item.name?.split(' ').slice(1).join(' '),
                      profile_picture: item.profile_picture ?? '',
                    })),
                  }))
                }}
                hasProfilePicture={true}
                isPaginated={true}
                currentPage={memberPage}
                selectionLimit={100}

                totalPages={membersQuery.data?.max_page || 1}
                onPageChange={(page) => setMemberPage(page)}
                onSearchChange={(setSearch) => setMemberSearch(setSearch)}
                isLoading={membersQuery.isLoading}
                hasNextPage={membersQuery.data?.has_next || false}
                isTop={true}
              />
              {errors.members && (
                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                  <Text>
                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                      <WarningIcon />
                    </Icon>
                    {errors.members}
                  </Text>
                </Box>
              )}
            </Box>
          </VStack>
    </FormModal>
  )
}

export default AddTeamsModal